/**
 * HeatmapGrid — Grid de setores colorido por nível de risco
 * Substitui organograma hierárquico (que não existe no banco)
 */
interface HeatmapCell {
  sector: string;
  avgScore: number;
  cargosAvaliados: number;
  riskLevel: "baixo" | "medio" | "alto" | "critico";
  color: string;
  labels?: string;
}

interface HeatmapGridProps {
  data: HeatmapCell[];
  title?: string;
}

const RISK_BG: Record<string, string> = {
  baixo: "bg-green-100 border-green-300",
  medio: "bg-amber-100 border-amber-300",
  alto: "bg-red-100 border-red-300",
  critico: "bg-red-200 border-red-400",
};

const RISK_TEXT: Record<string, string> = {
  baixo: "text-green-800",
  medio: "text-amber-800",
  alto: "text-red-800",
  critico: "text-red-900",
};

const RISK_LABEL: Record<string, string> = {
  baixo: "Baixo",
  medio: "Médio",
  alto: "Alto",
  critico: "Crítico",
};

export default function HeatmapGrid({ data, title = "Mapa de Calor Organizacional" }: HeatmapGridProps) {
  if (!data || data.length === 0) {
    return (
      <div className="p-6 text-center text-muted-foreground">
        <p className="text-sm">Nenhum setor avaliado ainda. Complete as avaliações para visualizar o mapa de calor.</p>
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-sm font-semibold text-[#002F59] mb-3">{title}</h3>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {data.map((cell, i) => (
          <div
            key={i}
            className={`p-3 rounded-lg border-2 ${RISK_BG[cell.riskLevel]} transition-all hover:shadow-md`}
          >
            <p className={`text-sm font-semibold ${RISK_TEXT[cell.riskLevel]} truncate`} title={cell.sector}>
              {cell.sector}
            </p>
            <div className="flex items-center justify-between mt-2">
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${RISK_TEXT[cell.riskLevel]}`} style={{ backgroundColor: `${cell.color}30` }}>
                {RISK_LABEL[cell.riskLevel]}
              </span>
              <span className="text-xs text-muted-foreground">
                {cell.cargosAvaliados} cargo{cell.cargosAvaliados !== 1 ? "s" : ""}
              </span>
            </div>
          </div>
        ))}
      </div>
      {/* Legend */}
      <div className="flex items-center gap-4 mt-4 pt-3 border-t">
        <span className="text-xs text-muted-foreground">Legenda:</span>
        {Object.entries(RISK_LABEL).map(([key, label]) => (
          <div key={key} className="flex items-center gap-1">
            <div className={`w-3 h-3 rounded ${RISK_BG[key].split(" ")[0]}`} />
            <span className="text-xs text-muted-foreground">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
